import { computed, reactive } from 'vue'
import type {
  CategoryRecord,
  ExportProgress,
  Id,
  ImageDraft,
  ImageMetaRecord,
  ImportOptions,
  ImportProgress,
  StorageInfo
} from '../types/catalog'
import {
  countImages,
  createCategory as createCategoryRecord,
  deleteCategory as deleteCategoryRecord,
  deleteImage as deleteImageRecord,
  getImagePage,
  getStorageInfo,
  importImages,
  initializeCatalog,
  listCategories,
  renameCategory as renameCategoryRecord,
  rotateImage as rotateImageRecord,
  updateImage as updateImageRecord
} from '../services/catalogService'
import { exportCatalogZip } from '../services/exportZip'

const PAGE_SIZE = 80

const state = reactive({
  initialized: false,
  categories: [] as CategoryRecord[],
  selectedCategoryId: null as Id | null,
  includeDescendants: false,
  search: '',
  images: [] as ImageMetaRecord[],
  imageOffset: 0,
  searchScanOffset: 0,
  hasMore: true,
  loadingImages: false,
  totalImages: 0,
  storage: { usage: 0, quota: 0, persisted: false } as StorageInfo,
  importing: false,
  importProgress: null as ImportProgress | null,
  exporting: false,
  exportProgress: null as ExportProgress | null
})

const selectedCategory = computed(() =>
  state.categories.find((category) => category.id === state.selectedCategoryId) ?? null
)

const categoryById = computed(() => new Map(state.categories.map((category) => [category.id, category])))

const selectedBreadcrumbs = computed(() => {
  const category = selectedCategory.value
  if (!category) return []
  return [...category.ancestorIds, category.id]
    .map((id) => categoryById.value.get(id))
    .filter((item): item is CategoryRecord => Boolean(item))
})

async function refreshOverview(): Promise<void> {
  const [categories, totalImages, storage] = await Promise.all([
    listCategories(),
    countImages(),
    getStorageInfo()
  ])
  state.categories = categories
  state.totalImages = totalImages
  state.storage = storage
}

async function refreshImages(): Promise<void> {
  state.images = []
  state.imageOffset = 0
  state.searchScanOffset = 0
  state.hasMore = true
  await loadMoreImages()
}

async function loadMoreImages(): Promise<void> {
  if (state.loadingImages || !state.hasMore) return
  state.loadingImages = true
  try {
    const result = await getImagePage({
      categoryId: state.selectedCategoryId,
      includeDescendants: state.includeDescendants,
      search: state.search,
      offset: state.imageOffset,
      scanOffset: state.searchScanOffset,
      pageSize: PAGE_SIZE
    })
    const existing = new Set(state.images.map((image) => image.id))
    state.images.push(...result.items.filter((image) => !existing.has(image.id)))
    state.imageOffset = result.nextOffset
    state.searchScanOffset = result.nextScanOffset
    state.hasMore = result.hasMore
  } finally {
    state.loadingImages = false
  }
}

async function initialize(): Promise<void> {
  if (state.initialized) return
  const initial = await initializeCatalog()
  state.selectedCategoryId = initial.id
  await refreshOverview()
  if (!state.categories.some((category) => category.id === state.selectedCategoryId)) {
    state.selectedCategoryId = state.categories[0]?.id ?? null
  }
  await refreshImages()
  state.initialized = true
}

async function selectCategory(id: Id | null): Promise<void> {
  if (state.selectedCategoryId === id) return
  state.selectedCategoryId = id
  await refreshImages()
}

async function setSearch(value: string): Promise<void> {
  state.search = value
  await refreshImages()
}

async function setIncludeDescendants(value: boolean): Promise<void> {
  state.includeDescendants = value
  await refreshImages()
}

async function createCategory(name: string, parentId: Id | null): Promise<CategoryRecord> {
  const record = await createCategoryRecord(name, parentId)
  await refreshOverview()
  return record
}

async function renameCategory(id: Id, name: string): Promise<void> {
  await renameCategoryRecord(id, name)
  await refreshOverview()
}

async function deleteCategory(id: Id): Promise<void> {
  await deleteCategoryRecord(id)
  if (state.selectedCategoryId === id) state.selectedCategoryId = null
  await refreshOverview()
  await refreshImages()
}

async function addImages(files: File[], options: ImportOptions, capturedAt?: number) {
  state.importing = true
  state.importProgress = { current: 0, total: files.length, fileName: '', imported: 0, failed: 0 }
  try {
    const result = await importImages(files, options, (progress) => {
      state.importProgress = progress
    }, capturedAt)
    await refreshOverview()
    await refreshImages()
    return result
  } finally {
    state.importing = false
    state.importProgress = null
  }
}

async function updateImage(id: Id, draft: ImageDraft): Promise<void> {
  await updateImageRecord(id, draft)
  await refreshOverview()
  await refreshImages()
}

async function rotateImage(id: Id, degrees: 90 | -90 | 180): Promise<void> {
  await rotateImageRecord(id, degrees)
  await refreshImages()
}

async function deleteImage(id: Id): Promise<void> {
  await deleteImageRecord(id)
  await refreshOverview()
  await refreshImages()
}

async function exportZip(rootCategoryId?: Id | null): Promise<void> {
  state.exporting = true
  state.exportProgress = { current: 0, total: 0, fileName: '' }
  try {
    await exportCatalogZip({
      rootCategoryId,
      onProgress: (progress) => {
        state.exportProgress = progress
      }
    })
  } finally {
    state.exporting = false
    state.exportProgress = null
  }
}

export function useCatalog() {
  return {
    state,
    selectedCategory,
    selectedBreadcrumbs,
    categoryById,
    initialize,
    refreshOverview,
    refreshImages,
    loadMoreImages,
    selectCategory,
    setSearch,
    setIncludeDescendants,
    createCategory,
    renameCategory,
    deleteCategory,
    addImages,
    updateImage,
    rotateImage,
    deleteImage,
    exportZip
  }
}
